import { useState } from "react";
import { Newspaper, Loader2, TrendingUp, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TAG = {
  Juros: "bg-primary/10 text-primary", Inflação: "bg-secondary/10 text-secondary",
  Câmbio: "bg-teal-100 text-teal-800", Mercado: "bg-amber-100 text-amber-800", Geral: "bg-stone-100 text-stone-700",
};

export default function News() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [loaded, setLoaded] = useState(false);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/news", { query: q || null });
      setItems(data.news || []); setLoaded(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="news-section">
      <div>
        <h2 className="font-heading text-3xl font-bold tracking-tight text-text-primary">Notícias Econômicas</h2>
        <p className="text-text-secondary font-body mt-1">Notícias reais e seu impacto prático nas suas finanças.</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-3 h-4 w-4 text-text-muted" />
          <Input data-testid="news-query" className="pl-9 rounded-md bg-white" value={q}
            onChange={(e) => setQ(e.target.value)} placeholder="Buscar tema (ex: Selic, dólar, PIB)" />
        </div>
        <Button data-testid="fetch-news" onClick={fetchNews} disabled={loading} className="rounded-md bg-primary hover:bg-primary/90 h-10">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Newspaper className="h-4 w-4 mr-2" />}
          {loading ? "Buscando..." : "Buscar notícias"}
        </Button>
      </div>

      {!loaded && !loading && (
        <div className="relative overflow-hidden rounded-md border border-stone-200 h-56">
          <img src="https://images.unsplash.com/photo-1769740029093-83f39268a6a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGdyb3d0aCUyMGFic3RyYWN0fGVufDB8fHx8MTc4NjQwNDE2M3ww&ixlib=rb-4.1.0&q=85"
            alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-primary/80 flex items-center justify-center">
            <p className="text-white font-body">Clique em "Buscar notícias" para ver os últimos impactos econômicos.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="news-list">
        {items.map((n, i) => (
          <div key={i} className="border border-stone-200 bg-white rounded-md p-6 hover:-translate-y-1 hover:shadow-sm transition-transform">
            <span className={`inline-block px-3 py-1 rounded text-xs font-body ${TAG[n.category] || TAG.Geral}`}>{n.category || "Geral"}</span>
            <h4 className="font-heading text-lg font-bold tracking-tight text-text-primary mt-3 leading-snug">{n.headline}</h4>
            <p className="text-text-secondary font-body mt-2 text-sm leading-relaxed">{n.summary}</p>
            <div className="mt-4 flex gap-2 items-start text-primary bg-primary/5 rounded p-3">
              <TrendingUp className="h-4 w-4 mt-0.5 shrink-0" />
              <p className="text-sm font-body">{n.impact}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
